import { makeObservable, observable, action } from 'mobx';
import { dataAccess } from 'ts/core/dataAccess/dataAccess';
import dayjs, { Dayjs } from 'dayjs';
import {
  getCurrentMonthRange,
  getLastMonthRange,
  getLastTwoWeeksRange,
  getLastWeekRange,
} from 'ts/helpers/getDefaultDate';
import { DATE_FORMAT } from 'ts/constants/constants';

interface IUser {
  name: string;
  email: string;
}

export interface IGlobalStore {
  contributorId: string | null;
  dateRange: [Dayjs | null, Dayjs | null] | null;
  userData: IUser;
  presetKey: string | null;
  isUnauthorized: boolean;
  setPreset: (presetKey: string) => void;
  setUserData: () => void;
  setContributorId: (id: string) => void;
  setDateRange: (dateRange: [Dayjs | null, Dayjs | null]) => void;
}

const LOCAL_STORAGE_KEY = 'globalStore';

const presetMap: Record<string, () => [Dayjs, Dayjs]> = {
  last_two_weeks: getLastTwoWeeksRange,
  last_week: getLastWeekRange,
  this_week: getLastWeekRange,
  last_month: getLastMonthRange,
  this_month: getCurrentMonthRange,
};

const matchPresetKey = (
  range: [Dayjs | null, Dayjs | null] | null
): string | null => {
  if (range === null) return null;
  const [start, end] = range;
  if (!start || !end) return null;

  for (const [key, getter] of Object.entries(presetMap)) {
    const [ps, pe] = getter();
    if (start.isSame(ps, 'day') && end.isSame(pe, 'day')) {
      return key;
    }
  }
  return null;
};

const toDayjsOrNull = (v: unknown): Dayjs | null => {
  if (!v) return null;
  const d = dayjs(String(v));
  return d.isValid() ? d : null;
};

const loadFromStorage = (): {
  contributorId: string | null;
  dateRange: [Dayjs | null, Dayjs | null] | null;
  presetKey: string | null;
} => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw)
      return {
        contributorId: null,
        dateRange: getLastTwoWeeksRange(),
        presetKey: 'last_two_weeks',
      };

    const parsed = JSON.parse(raw);

    if (parsed.presetKey && presetMap[parsed.presetKey]) {
      return {
        contributorId: parsed.contributorId ?? null,
        dateRange: presetMap[parsed.presetKey](),
        presetKey: parsed.presetKey,
      };
    }

    return {
      contributorId: parsed.contributorId ?? null,
      dateRange: Array.isArray(parsed.dateRange)
        ? [
          toDayjsOrNull(parsed.dateRange[0]),
          toDayjsOrNull(parsed.dateRange[1]),
        ]
        : null,
      presetKey: null,
    };
  } catch (e) {
    console.warn('Failed to load filters from localStorage:', e);
    return {
      contributorId: null,
      dateRange: getLastTwoWeeksRange(),
      presetKey: 'last_two_weeks',
    };
  }
};

const saveToStorage = (
  contributorId: string | null,
  dateRange: [Dayjs | null, Dayjs | null] | null,
  presetKey: string | null
) => {
  try {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        contributorId,
        presetKey: presetKey ?? null,
        dateRange: presetKey
          ? null
          : dateRange
            ? dateRange.map((d) => (d ? d.format(DATE_FORMAT) : null))
            : null,
      })
    );
  } catch (e) {
    console.warn('Failed to save filters to localStorage:', e);
  }
};

class GlobalStore implements IGlobalStore {
  contributorId: string | null = null;
  dateRange: [Dayjs | null, Dayjs | null] | null = null;
  presetKey: string | null = null;
  userData: IUser = { name: '', email: '' };
  isUnauthorized: boolean = false;

  constructor() {
    const storedData = loadFromStorage();
    this.contributorId = storedData.contributorId ?? null;
    this.dateRange = storedData.dateRange ?? null;
    this.presetKey = storedData.presetKey;

    makeObservable(this, {
      contributorId: observable,
      dateRange: observable,
      userData: observable,
      isUnauthorized: observable,
      presetKey: observable,
      setUserData: action,
      setContributorId: action,
      setDateRange: action,
    });
  }

  setContributorId(id: string) {
    this.contributorId = id;
    saveToStorage(this.contributorId, this.dateRange, this.presetKey);
  }

  setDateRange(dateRange: [Dayjs | null, Dayjs | null] | null) {
    this.dateRange = dateRange;
    const matchedKey = matchPresetKey(dateRange);
    this.presetKey = matchedKey;
    saveToStorage(this.contributorId, this.dateRange, this.presetKey);
  }

  setPreset(presetKey: string) {
    this.presetKey = presetKey;
    this.dateRange = presetMap[presetKey]();
    saveToStorage(this.contributorId, this.dateRange, this.presetKey);
  }

  async setUserData() {
    const [userData, error] = await dataAccess.getUser();
    if (error?.status === 401) {
      this.isUnauthorized = true;
      this.userData = { name: '', email: '' };
      return;
    }
    if (userData) {
      this.isUnauthorized = false;
      this.userData = userData;
    }
  }
}

const globalStore = new GlobalStore();

export default globalStore;
